import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { useChipOverflow } from '~settings/hooks/useChipOverflow';

import { AppliedFilter } from '../AppliedFilter';
import type { FilterEntity } from './hooks/useFilterChips';
import { useFilterChips } from './hooks/useFilterChips';

type FilterListProps = {
  entity: FilterEntity;
};

const FilterList = ({ entity }: FilterListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('settings');
  const selectedFilters = useFilterChips(entity);

  const handleDelete = (key: string, value: string) => {
    const currList = searchParams.get(key)?.split(',');
    if (!currList) {
      return;
    }

    const newList = currList.filter((val) => val !== value);

    if (newList.length) {
      searchParams.set(key, newList.join(','));
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const { expanded, isOverflow, allowedHeight, setExpanded } = useChipOverflow(
    containerRef,
    selectedFilters,
  );

  if (!selectedFilters || selectedFilters.length === 0) {
    return (
      <div className="h-full items-center text-body-sm text-subtle">
        {t('noFilters')}
      </div>
    );
  }

  return (
    <div className="h-full">
      <div
        className="flex flex-wrap gap-sm overflow-hidden p-px"
        style={{
          maxHeight: expanded ? 'none' : allowedHeight,
        }}
        ref={containerRef}
      >
        {selectedFilters.map(({ title, value }) =>
          value.map((item: { id: string; name: string }) => (
            <AppliedFilter
              key={item.id}
              title={title}
              value={item}
              onRemove={handleDelete}
            />
          )),
        )}
      </div>
      {isOverflow ? (
        <Button
          className="mt-sm cursor-pointer text-body-sm text-subtle"
          onClick={() => setExpanded(!expanded)}
          variant="tertiary"
        >
          {expanded ? t('showLess') : t('showMore')}
        </Button>
      ) : null}
    </div>
  );
};

FilterList.displayName = 'FilterList';
export { FilterList };
